import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import { trackError } from '../../lib/sentry';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Track error with Sentry
    trackError(error, {
      errorInfo,
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error!}
          resetError={this.resetError}
          errorInfo={this.state.errorInfo || undefined}
        />
      );
    }

    return this.props.children;
  }
}

// Default Error Fallback Component
const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetError, 
  errorInfo 
}) => {
  const [showDetails, setShowDetails] = React.useState(false);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReportBug = () => {
    const subject = encodeURIComponent(`Bug Report: ${error.message}`);
    const body = encodeURIComponent(`
Error: ${error.message}
Stack: ${error.stack}
Component Stack: ${errorInfo?.componentStack || 'Not available'}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
    `);
    
    window.open(`mailto:support@aegisvault.com?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <Card className="p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Oops! Something went wrong
          </h1>
          
          <p className="text-gray-600 mb-6">
            We're sorry, but something unexpected happened. Our team has been notified 
            and is working to fix the issue.
          </p>

          <div className="space-y-3 mb-6">
            <Button
              onClick={resetError}
              icon={RefreshCw}
              className="w-full"
            >
              Try Again
            </Button>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleGoHome}
                icon={Home}
                className="flex-1"
              >
                Go Home
              </Button>
              
              <Button
                variant="outline"
                onClick={handleReload}
                icon={RefreshCw}
                className="flex-1"
              >
                Reload Page
              </Button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="mb-4"
            >
              {showDetails ? 'Hide' : 'Show'} Error Details
            </Button>

            {showDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-left bg-gray-50 rounded-lg p-4 mb-4"
              >
                <h3 className="font-semibold text-gray-900 mb-2">Error Details:</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>Message:</strong>
                    <p className="text-red-600 font-mono">{error.message}</p>
                  </div>
                  
                  {error.stack && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleReportBug}
              icon={Bug}
              className="text-gray-500"
            >
              Report Bug
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

// Specific Error Fallbacks for different contexts
export const DocumentErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetError 
}) => (
  <Card className="p-6 text-center">
    <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      Failed to Load Documents
    </h3>
    <p className="text-gray-600 mb-4">
      {error.message || 'There was an error loading your documents.'}
    </p>
    <Button onClick={resetError} size="sm">
      Try Again
    </Button>
  </Card>
);

export const UploadErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetError 
}) => (
  <Card className="p-6 text-center border-red-200 bg-red-50">
    <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-red-900 mb-2">
      Upload Failed
    </h3>
    <p className="text-red-700 mb-4">
      {error.message || 'There was an error uploading your files.'}
    </p>
    <Button onClick={resetError} size="sm" variant="outline">
      Try Again
    </Button>
  </Card>
);

export default ErrorBoundary;